import type { GooglePlace } from "@albertine/types";
import classNames from "classnames";
import { useRef } from "react";
import { useComponentState } from "../../utils/customHook.util";
import GooglePlaceInputField from "../GooglePlaceInputField/GooglePlaceInputField";
import { Flex } from "./Flex";
import "./SelectLocations.css";
import { type ThemeLocation, themeLocationToText } from "@albertine/types/theme";
import { excludeUndefined } from "../../common/utils/array.util";
import { GooglePlaceLocationSearchFields } from "../../common/utils/google/place.util";
import { sortByStringComparator } from "../../common/utils/sort.util";
import { placeToLocation } from "../../common/utils/theme.util";
import { EventEmitter } from "../../EventEmitter";
import Button from "./Buttons";
import Icon from "./Icon";
import Label from "./Label";

type SelectLocationsProps = {
    initialValues: ThemeLocation[];
    disabled?: boolean;
    onChange: (locations: ThemeLocation[]) => void;
};

type SelectLocationsState = {
    locations: ThemeLocation[];
};

function SelectLocations(props: SelectLocationsProps) {
    const { initialValues, onChange, disabled = false } = props;
    const { state, setState } = useComponentState<SelectLocationsState>({
        locations: initialValues,
    });
    const googlePlaceInputField = useRef<{
        input?: () => HTMLInputElement | null;
        clear: () => void | null;
    }>(null);

    function add(placeToAdd: GooglePlace) {
        const locationToAdd = placeToLocation(placeToAdd);

        if (!locationToAdd) {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Unable to add this location",
                },
            });
            return;
        }

        const exists =
            state.locations.filter(
                (location) =>
                    location.city === locationToAdd.city &&
                    location.countryCode === locationToAdd.countryCode,
            ).length !== 0;

        if (!exists) {
            const newPlaces = [...state.locations, locationToAdd];
            setState({
                locations: newPlaces.sort(
                    sortByStringComparator((location) =>
                        location?.city ? location.city : location?.country,
                    ),
                ),
            });
            onChange(newPlaces);
        } else {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: `"${themeLocationToText(
                        locationToAdd,
                    )}" is already selected`,
                },
            });
        }

        googlePlaceInputField.current?.clear();
    }

    function remove(removeIndex: number) {
        const newLocations = state.locations
            .sort(
                sortByStringComparator((location) =>
                    location?.city ? location.city : location?.country,
                ),
            )
            .map((it, index) => (index !== removeIndex ? it : undefined))
            .filter(excludeUndefined);
        setState({
            locations: newLocations,
        });
        onChange(newLocations);
    }

    return (
        <Flex.Column
            gap="large"
            className={classNames("select-locations", {
                "select-locations__disabled": disabled,
            })}
        >
            <GooglePlaceInputField
                label="Location"
                placeholder="Search by city or country"
                ref={googlePlaceInputField}
                searchFor="citiesAndCountriesOnly"
                displayLocationPin
                fields={GooglePlaceLocationSearchFields}
                onSelect={(place) => {
                    if (!disabled) {
                        add(place);
                    }
                }}
            />

            {state.locations.length !== 0 && (
                <Flex.Column gap="medium">
                    <h6>Selected locations</h6>

                    <Flex.Column gap="small">
                        {state.locations.map((place, index) => {
                            const text = themeLocationToText(place);

                            return (
                                <Flex.Row
                                    key={text}
                                    className="select-locations__selected-item"
                                    justifyContent="space-between"
                                >
                                    <Label.Medium.SemiBold.N10>
                                        {text}
                                    </Label.Medium.SemiBold.N10>
                                    <Button.Ghost
                                        onClick={() => {
                                            if (!disabled) {
                                                remove(index);
                                            }
                                        }}
                                    >
                                        <Icon.Close.Medium.N10 />
                                    </Button.Ghost>
                                </Flex.Row>
                            );
                        })}
                    </Flex.Column>
                </Flex.Column>
            )}
        </Flex.Column>
    );
}

export default SelectLocations;
